import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { calendarApi, errorMessage } from '@/lib/api';
import type { Booking, BookingStats, CalendarIntegration, CalendarProvider, Paginated, Slot } from '@/types';

/** A company's own OAuth application, as the API reports it. Never carries the secret. */
export interface CalendarApp {
  provider: CalendarProvider;
  configured: boolean;
  available: boolean;
  source: 'company' | 'deployment' | null;
  /** The URI the company must register in their own app. */
  redirectUri: string;
  clientId?: string;
  tenantId?: string | null;
  enabled?: boolean;
  clientSecretSet?: boolean;
  updatedAt?: string;
}

export interface CalendarState {
  integrations: CalendarIntegration[];
  apps: CalendarApp[];
  slots: Slot[];
  bookings: Booking[];
  bookingPagination: Paginated<Booking>['pagination'] | null;
  stats: BookingStats | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: CalendarState = {
  integrations: [],
  apps: [],
  slots: [],
  bookings: [],
  bookingPagination: null,
  stats: null,
  loading: false,
  saving: false,
  error: null,
};

export const fetchIntegrations = createAsyncThunk<CalendarIntegration[], void, { rejectValue: string }>(
  'calendar/fetchIntegrations',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await calendarApi.get<{ data: CalendarIntegration[] }>('/integrations');
      return data.data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not load calendar connections'));
    }
  }
);

export const fetchCalendarApps = createAsyncThunk<CalendarApp[], void, { rejectValue: string }>(
  'calendar/fetchApps',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await calendarApi.get<{ data: CalendarApp[] }>('/integrations/apps');
      return data.data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not load the calendar applications'));
    }
  }
);

export const saveCalendarApp = createAsyncThunk<
  CalendarApp,
  { provider: CalendarProvider; clientId: string; clientSecret?: string; tenantId?: string; enabled?: boolean },
  { rejectValue: string }
>('calendar/saveApp', async ({ provider, ...body }, { rejectWithValue }) => {
  try {
    // The secret is omitted rather than sent empty when unchanged: an empty string would
    // overwrite a working credential with nothing.
    const payload = Object.fromEntries(Object.entries(body).filter(([, v]) => v !== undefined && v !== ''));
    const { data } = await calendarApi.put<CalendarApp>(`/integrations/apps/${provider}`, payload);
    return { ...data, provider };
  } catch (err) {
    return rejectWithValue(errorMessage(err, 'Could not save the application'));
  }
});

export const deleteCalendarApp = createAsyncThunk<CalendarProvider, CalendarProvider, { rejectValue: string }>(
  'calendar/deleteApp',
  async (provider, { rejectWithValue }) => {
    try {
      await calendarApi.delete(`/integrations/apps/${provider}`);
      return provider;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not remove the application'));
    }
  }
);

export const connectCalendar = createAsyncThunk<{ authorizationUrl: string }, CalendarProvider, { rejectValue: string }>(
  'calendar/connect',
  async (provider, { rejectWithValue }) => {
    try {
      const { data } = await calendarApi.post<{ authorizationUrl: string }>(`/integrations/${provider}/connect`);
      return data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not start the calendar connection'));
    }
  }
);

export const saveCalendarSettings = createAsyncThunk<
  CalendarIntegration,
  { provider: CalendarProvider; settings: Record<string, unknown> },
  { rejectValue: string }
>('calendar/saveSettings', async ({ provider, settings }, { rejectWithValue }) => {
  try {
    const { data } = await calendarApi.patch<CalendarIntegration>(`/integrations/${provider}/settings`, settings);
    return data;
  } catch (err) {
    return rejectWithValue(errorMessage(err, 'Could not save your availability preferences'));
  }
});

export const disconnectCalendar = createAsyncThunk<string, string, { rejectValue: string }>(
  'calendar/disconnect',
  async (integrationId, { rejectWithValue }) => {
    try {
      await calendarApi.delete(`/integrations/${integrationId}`);
      return integrationId;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not disconnect the calendar'));
    }
  }
);

export const fetchSlots = createAsyncThunk<Slot[], Record<string, unknown> | void, { rejectValue: string }>(
  'calendar/fetchSlots',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await calendarApi.get<{ data: Slot[] }>('/slots', { params: params ?? undefined });
      return data.data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not load availability'));
    }
  }
);

export const generateSlots = createAsyncThunk<
  { created: number; skipped: number },
  Record<string, unknown>,
  { rejectValue: string }
>('calendar/generateSlots', async (payload, { rejectWithValue }) => {
  try {
    const { data } = await calendarApi.post<{ created: number; skipped: number }>('/slots/generate', payload);
    return data;
  } catch (err) {
    return rejectWithValue(errorMessage(err, 'Could not generate availability'));
  }
});

export const deleteSlot = createAsyncThunk<string, string, { rejectValue: string }>(
  'calendar/deleteSlot',
  async (slotId, { rejectWithValue }) => {
    try {
      await calendarApi.delete(`/slots/${slotId}`);
      return slotId;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not remove that slot'));
    }
  }
);

export const fetchBookings = createAsyncThunk<
  Paginated<Booking>,
  Record<string, unknown> | void,
  { rejectValue: string }
>('calendar/fetchBookings', async (params, { rejectWithValue }) => {
  try {
    const { data } = await calendarApi.get<Paginated<Booking>>('/bookings', { params: params ?? undefined });
    return data;
  } catch (err) {
    return rejectWithValue(errorMessage(err, 'Could not load bookings'));
  }
});

export const decideBooking = createAsyncThunk<
  Booking,
  { id: string; decision: 'approve' | 'reject' | 'cancel'; notes?: string },
  { rejectValue: string }
>('calendar/decideBooking', async ({ id, decision, notes }, { rejectWithValue }) => {
  try {
    const { data } = await calendarApi.patch<Booking>(`/bookings/${id}/${decision}`, { notes });
    return data;
  } catch (err) {
    return rejectWithValue(errorMessage(err, 'Could not update the booking'));
  }
});

export const fetchBookingStats = createAsyncThunk<BookingStats, void, { rejectValue: string }>(
  'calendar/stats',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await calendarApi.get<BookingStats>('/bookings/stats');
      return data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not load booking statistics'));
    }
  }
);

const calendarSlice = createSlice({
  name: 'calendar',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchIntegrations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchIntegrations.fulfilled, (state, action) => {
        state.loading = false;
        state.integrations = action.payload;
      })
      .addCase(fetchIntegrations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(fetchCalendarApps.fulfilled, (state, action) => {
        state.apps = action.payload;
      })
      .addCase(saveCalendarApp.pending, (state) => {
        state.saving = true;
      })
      .addCase(saveCalendarApp.fulfilled, (state, action) => {
        state.saving = false;
        const index = state.apps.findIndex((a) => a.provider === action.payload.provider);
        // Merged rather than replaced: the save response carries the stored application,
        // and the row also holds the redirect URI and availability the list computed.
        if (index >= 0) state.apps[index] = { ...state.apps[index]!, ...action.payload, configured: true };
      })
      .addCase(saveCalendarApp.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload ?? null;
      })
      .addCase(deleteCalendarApp.fulfilled, (state, action) => {
        const index = state.apps.findIndex((a) => a.provider === action.payload);
        if (index >= 0) {
          state.apps[index] = {
            ...state.apps[index]!,
            configured: false,
            clientId: undefined,
            tenantId: null,
            clientSecretSet: false,
            // Availability can survive removal, because the deployment may still have
            // environment credentials to fall back to.
            source: null,
          };
        }
      })
      .addCase(saveCalendarSettings.fulfilled, (state, action) => {
        const index = state.integrations.findIndex((i) => i.provider === action.payload.provider);
        if (index >= 0) state.integrations[index] = action.payload;
      })
      .addCase(disconnectCalendar.fulfilled, (state, action) => {
        state.integrations = state.integrations.map((i) =>
          i.id === action.payload ? { provider: i.provider, connected: false, status: null, available: true } : i
        );
      })
      .addCase(fetchSlots.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSlots.fulfilled, (state, action) => {
        state.loading = false;
        state.slots = action.payload;
      })
      .addCase(fetchSlots.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(generateSlots.pending, (state) => {
        state.saving = true;
      })
      .addCase(generateSlots.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(generateSlots.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload ?? null;
      })
      .addCase(deleteSlot.fulfilled, (state, action) => {
        state.slots = state.slots.filter((slot) => slot.id !== action.payload);
      })
      .addCase(fetchBookings.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBookings.fulfilled, (state, action) => {
        state.loading = false;
        state.bookings = action.payload.data;
        state.bookingPagination = action.payload.pagination;
      })
      .addCase(fetchBookings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(decideBooking.fulfilled, (state, action) => {
        const index = state.bookings.findIndex((b) => b.id === action.payload.id);
        if (index >= 0) state.bookings[index] = action.payload;
      })
      .addCase(fetchBookingStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      });
  },
});

export default calendarSlice.reducer;
